import { useEffect, useRef, useState, useCallback } from 'react';
import { loadModules, loadCss } from 'esri-loader';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  ChevronLeft, 
  ChevronRight, 
  ArrowLeft, 
  Save,
  Trash2,
  Plus,
  Loader2,
  AlertCircle,
  Image,
  ZoomIn
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useRunWaypoints, type RunWaypoint } from '@/hooks/useRunWaypoints';
import AIWaypointBadge from './AIWaypointBadge';
import { Alert, AlertDescription } from './ui/alert';
import { Progress } from './ui/progress';
import PhotoLightbox from './PhotoLightbox';

interface RunWaypointReviewProps {
  runId: string;
  trackGeometry?: any;
  onComplete: () => void;
  onBack: () => void;
}

const RunWaypointReview = ({ 
  runId, 
  trackGeometry,
  onComplete,
  onBack
}: RunWaypointReviewProps) => {
  const mapDiv = useRef<HTMLDivElement>(null);
  const { waypoints: dbWaypoints, isLoading, saveWaypoints } = useRunWaypoints(runId);
  const [waypoints, setWaypoints] = useState<RunWaypoint[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [mapView, setMapView] = useState<any>(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [deletedIndices, setDeletedIndices] = useState<Set<number>>(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const { toast } = useToast();

  // Layer refs
  const trackLayerRef = useRef<any>(null);
  const waypointLayerRef = useRef<any>(null);
  const isInitializingRef = useRef(false);

  // Map detection type to waypoint symbol/type
  const mapDetectionTypeToSymbol = (detectionType?: string | null): string => {
    const mapping: { [key: string]: string } = {
      'stop': 'rest_stop',
      'turnaround': 'turnaround',
      'elevation_change': 'climb',
      'photo_cluster': 'scenic',
      'hiking': 'trailhead',
      'manual': 'poi'
    };
    return detectionType ? (mapping[detectionType] || 'poi') : 'poi';
  };

  // Initialize waypoints from database
  useEffect(() => {
    console.log('RunWaypointReview: dbWaypoints updated', { count: dbWaypoints.length });
    if (dbWaypoints.length > 0) {
      // Map waypoints and ensure symbol is set
      const mappedWaypoints = dbWaypoints.map(wp => ({
        ...wp,
        symbol: wp.symbol || mapDetectionTypeToSymbol(wp.detection_type)
      }));
      setWaypoints(mappedWaypoints);
    }
  }, [dbWaypoints]);

  // Initialize map when container is ready
  useEffect(() => {
    if (!mapDiv.current || mapView || isLoading) {
      console.log('RunWaypointReview: Map initialization check', {
        hasMapDiv: !!mapDiv.current,
        isLoading,
        hasMapView: !!mapView
      });
      return;
    }
    
    console.log('RunWaypointReview: Ready to initialize map');
    initializeMap();
  }, [mapDiv.current, isLoading, mapView]);

  // Stage 2: Initialize map instance
  const initializeMap = async () => {
    console.log('RunWaypointReview: initializeMap called', { 
      hasMapView: !!mapView, 
      hasMapDiv: !!mapDiv.current 
    });
    
    if (mapView) {
      console.log('RunWaypointReview: Map view already exists, skipping initialization');
      return;
    }
    
    if (!mapDiv.current) {
      console.log('RunWaypointReview: Map div not available, cannot initialize');
      return;
    }

    try {
      console.log('RunWaypointReview: Starting map initialization...');
      isInitializingRef.current = true;
      setIsMapLoading(true);
      setMapError(null);

      console.log('RunWaypointReview: Loading ArcGIS modules...');
      // Load ArcGIS CSS and modules for map with GeoJSONLayers
      try { loadCss(); } catch { /* ignore */ }
      const [
        Map, 
        MapView, 
        VectorTileLayer,
        GeoJSONLayer
      ] = await loadModules([
        'esri/Map',
        'esri/views/MapView',
        'esri/layers/VectorTileLayer',
        'esri/layers/GeoJSONLayer'
      ]);

      console.log('RunWaypointReview: ArcGIS modules loaded, creating layers...');
      
      // Create basemap layer - using portalItem like TrailMap
      const basemapLayer = new VectorTileLayer({
        portalItem: { id: "ef4bc627ccab4a80990ed476d53020d6" }
      });

      // Create GeoJSONLayer for track
      const trackLayer = new GeoJSONLayer({
        url: `https://ogripwmsopctiljhqivm.supabase.co/functions/v1/run-tracks-geojson?run_id=${runId}`,
        title: 'Run Track',
        renderer: {
          type: "simple",
          symbol: {
            type: "simple-line",
            color: [0, 122, 255, 0.8],
            width: 3
          }
        }
      });
      trackLayerRef.current = trackLayer;

      // Create GeoJSONLayer for waypoints
      const waypointLayer = new GeoJSONLayer({
        url: `https://ogripwmsopctiljhqivm.supabase.co/functions/v1/run-waypoints-geojson?run_id=${runId}`,
        title: 'Run Waypoints',
        popupEnabled: false,
        outFields: ['*'],
        renderer: {
          type: "simple",
          symbol: {
            type: "simple-marker",
            color: [0, 186, 255, 0.8],
            size: 10,
            outline: {
              color: [0, 78, 107],
              width: 1
            }
          }
        }
      });
      waypointLayerRef.current = waypointLayer;

      const map = new Map({
        layers: [basemapLayer, trackLayer, waypointLayer]
      });
      
      console.log('RunWaypointReview: Creating MapView...');
      const view = new MapView({
        container: mapDiv.current,
        map: map,
        ui: {
          components: ['zoom']
        }
      });

      // Start layer loading immediately (don't await view.when first)
      const layerPromises = Promise.all([
        trackLayer.when(),
        waypointLayer.when()
      ]);

      console.log('RunWaypointReview: Waiting for layers to load...');
      layerPromises.then(() => {
        console.log('RunWaypointReview: Layers loaded, zooming to first waypoint');
        
        // Zoom to first waypoint if available
        if (waypoints.length > 0 && waypoints[0].geom?.coordinates) {
          loadModules(['esri/geometry/Point']).then(([Point]) => {
            const [lng, lat] = waypoints[0].geom.coordinates;
            const point = new Point({ longitude: lng, latitude: lat });
            view.goTo({ target: point, zoom: 16 });
          }).catch(err => console.warn('Error zooming to first waypoint:', err));
        } else if (trackLayer.fullExtent) {
          // Fallback to track extent if no waypoints
          view.goTo(trackLayer.fullExtent.expand(1.2));
        }
        
        setIsMapLoading(false);
        isInitializingRef.current = false;
      }).catch((err: any) => {
        console.warn('RunWaypointReview: Layer load error:', err);
        setIsMapLoading(false);
        isInitializingRef.current = false;
      });
      
      setMapView(view);

      console.log('RunWaypointReview: Map loaded with GeoJSON layers');

    } catch (error) {
      console.error('RunWaypointReview: Map initialization error:', error);
      setMapError('Failed to load map. You can still edit waypoint details.');
      setIsMapLoading(false);
      isInitializingRef.current = false;
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Don't cleanup if still initializing
      if (isInitializingRef.current) {
        console.log('RunWaypointReview: Skipping cleanup - still initializing');
        return;
      }

      console.log('RunWaypointReview: Cleaning up map view');
      if (mapView) {
        try {
          mapView.destroy();
        } catch (err) {
          console.warn('Error destroying mapView:', err);
        }
      }
      if (trackLayerRef.current) {
        try {
          trackLayerRef.current.destroy?.();
        } catch (err) {
          console.warn('Error destroying trackLayer:', err);
        }
        trackLayerRef.current = null;
      }
      if (waypointLayerRef.current) {
        try {
          waypointLayerRef.current.destroy?.();
        } catch (err) {
          console.warn('Error destroying waypointLayer:', err);
        }
        waypointLayerRef.current = null;
      }
    };
  }, [mapView]);

  const currentWaypoint = waypoints[currentIndex];
  const progress = ((currentIndex + 1) / waypoints.length) * 100;
  const activeWaypoints = waypoints.filter((_, idx) => !deletedIndices.has(idx));

  // Zoom and highlight current waypoint when index changes
  useEffect(() => {
    if (!mapView || waypoints.length === 0 || !waypointLayerRef.current || !currentWaypoint?.geom?.coordinates) {
      return;
    }

    loadModules(['esri/geometry/Point', 'esri/layers/support/FeatureEffect']).then(([Point, FeatureEffect]) => {
      // Create point geometry from waypoint coordinates
      const [lng, lat] = currentWaypoint.geom.coordinates;
      const point = new Point({ longitude: lng, latitude: lat });
      
      // Zoom to waypoint
      mapView.goTo({ target: point, zoom: 16 });
      
      // Apply feature effect to highlight current waypoint
      waypointLayerRef.current.featureEffect = new FeatureEffect({
        filter: { where: `id = '${currentWaypoint.id}'` },
        excludedEffect: "opacity(30%) grayscale(90%)"
      });
    }).catch(err => {
      console.warn('Error applying waypoint highlight:', err);
    });
  }, [currentIndex, mapView, waypoints, currentWaypoint]);


  const handleUpdateWaypoint = (field: keyof RunWaypoint, value: any) => {
    const updated = [...waypoints];
    updated[currentIndex] = { ...updated[currentIndex], [field]: value };
    setWaypoints(updated);
  };

  const handleNext = () => {
    if (currentIndex < waypoints.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setShowSummary(true);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleDelete = () => {
    setDeletedIndices(new Set([...deletedIndices, currentIndex]));
    handleNext();
  };

  const handleDeleteFromSummary = (waypointIndex: number) => {
    setDeletedIndices(new Set([...deletedIndices, waypointIndex]));
  };

  const handleSaveAndContinue = async () => {
    setIsSaving(true);
    try {
      const finalWaypoints = waypoints.filter((_, idx) => !deletedIndices.has(idx));
      // Get IDs of waypoints that were explicitly deleted (not temp IDs)
      const deletedIds = waypoints
        .filter((wp, idx) => deletedIndices.has(idx) && wp.id && !wp.id.startsWith('temp-'))
        .map(wp => wp.id);
      const success = await saveWaypoints(finalWaypoints, deletedIds);
      
      if (success) {
        toast({
          title: "Success",
          description: `Saved ${finalWaypoints.length} waypoint${finalWaypoints.length !== 1 ? 's' : ''}`,
        });
        onComplete();
      } else {
        toast({
          title: "Error",
          description: "Failed to save waypoints",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error saving waypoints:', error);
      toast({
        title: "Error",
        description: "An error occurred while saving",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const openLightbox = (photoUrls: string[], startIndex: number = 0) => {
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  };

  // Get photo count for a waypoint
  const getPhotoCount = (wp: RunWaypoint): number => {
    return Array.isArray(wp.photo_urls) ? wp.photo_urls.length : 0;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (waypoints.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Waypoints Detected</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            No waypoints were automatically detected from your photos. You can skip this step or add waypoints manually later.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button onClick={onComplete}>
              Skip to Details
              <ChevronRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Summary view
  if (showSummary) {
    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle>Review Waypoints</CardTitle>
          <p className="text-sm text-muted-foreground">
            {activeWaypoints.length} waypoint{activeWaypoints.length !== 1 ? 's' : ''} will be saved
          </p>
        </CardHeader>
        <CardContent className="p-4 md:p-6 space-y-4">
          <div className="space-y-2 max-h-[50vh] md:max-h-96 overflow-y-auto">
            {activeWaypoints.map((wp, idx) => {
              const originalIndex = waypoints.indexOf(wp);
              const photoCount = getPhotoCount(wp);
              return (
                <div key={wp.id} className="flex items-start gap-3 p-3 border rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium">{wp.name || 'Unnamed Waypoint'}</span>
                      {wp.auto_generated && wp.detection_type && (
                        <AIWaypointBadge 
                          detectionType={wp.detection_type}
                          autoGenerated={wp.auto_generated}
                        />
                      )}
                      {/* Phase 6: Photo count badge */}
                      {photoCount > 0 && (
                        <Badge variant="secondary" className="gap-1">
                          <Image className="h-3 w-3" />
                          {photoCount}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {wp.description || 'No description'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowSummary(false);
                        setCurrentIndex(originalIndex);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteFromSummary(originalIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col md:flex-row gap-2 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowSummary(false)}
              className="w-full md:w-auto"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Editing
            </Button>
            <Button 
              onClick={handleSaveAndContinue}
              disabled={isSaving}
              className="w-full md:flex-1"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save & Continue
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Single waypoint editor view
  const currentPhotoUrls = currentWaypoint?.photo_urls || [];
  const currentPhotoCount = Array.isArray(currentPhotoUrls) ? currentPhotoUrls.length : 0;

  return (
    <>
      <Card className="flex flex-col h-[calc(100vh-12rem)] md:h-auto">
        {/* Sticky Header */}
        <CardHeader className="sticky top-0 z-10 bg-card border-b p-4 md:relative md:border-0 md:p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <CardTitle>Review Waypoints</CardTitle>
              {/* Photo indicator in header */}
              {currentPhotoCount > 0 && (
                <Badge variant="secondary" className="gap-1">
                  <Image className="h-3 w-3" />
                  {currentPhotoCount} photo{currentPhotoCount !== 1 ? 's' : ''}
                </Badge>
              )}
            </div>
            <Badge variant="secondary">
              {currentIndex + 1} of {waypoints.length}
            </Badge>
          </div>
          <Progress value={progress} className="h-2" />
        </CardHeader>
        
        {/* Scrollable Content */}
        <CardContent className="flex-1 overflow-y-auto p-4 md:p-6 space-y-3 md:space-y-6">
          {/* Map View */}
          <div className="space-y-2">
            <Label>Location on Map</Label>
            <div className="relative">
              <div 
                ref={mapDiv} 
                className="h-[200px] md:h-[400px] w-full rounded-lg border"
                style={{ background: '#f8f9fa' }}
              />
              {isMapLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-background/80 rounded-lg">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
              {mapError && (
                <Alert className="mt-2">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{mapError}</AlertDescription>
                </Alert>
              )}
            </div>
          </div>

          {/* Waypoint Details */}
          <div className="space-y-3 md:space-y-4">
            {currentWaypoint.auto_generated && currentWaypoint.detection_type && (
              <AIWaypointBadge 
                detectionType={currentWaypoint.detection_type}
                autoGenerated={currentWaypoint.auto_generated}
              />
            )}

            <div className="space-y-2">
              <Label htmlFor="waypoint-name">Title</Label>
              <Input
                id="waypoint-name"
                value={currentWaypoint.name || ''}
                onChange={(e) => handleUpdateWaypoint('name', e.target.value)}
                placeholder={currentWaypoint.ai_suggested_title || 'Enter waypoint title'}
              />
              {currentWaypoint.ai_suggested_title && (
                <p className="text-xs text-muted-foreground">
                  AI suggestion: {currentWaypoint.ai_suggested_title}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="waypoint-type">Type</Label>
              <Select
                value={currentWaypoint.symbol || 'poi'}
                onValueChange={(value) => handleUpdateWaypoint('symbol', value)}
              >
                <SelectTrigger id="waypoint-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-background z-50">
                  <SelectItem value="poi">Point of Interest</SelectItem>
                  <SelectItem value="rest_stop">Rest Stop / Break</SelectItem>
                  <SelectItem value="turnaround">Turnaround Point</SelectItem>
                  <SelectItem value="climb">Climb / Descent</SelectItem>
                  <SelectItem value="scenic">Scenic Viewpoint</SelectItem>
                  <SelectItem value="trailhead">Trailhead</SelectItem>
                  <SelectItem value="obstacle">Obstacle</SelectItem>
                  <SelectItem value="hazard">Hazard</SelectItem>
                  <SelectItem value="water_crossing">Water Crossing</SelectItem>
                  <SelectItem value="camp">Campsite</SelectItem>
                  <SelectItem value="landmark">Landmark</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="waypoint-description">Description</Label>
              <Textarea
                id="waypoint-description"
                value={currentWaypoint.description || ''}
                onChange={(e) => handleUpdateWaypoint('description', e.target.value)}
                placeholder="Add details about this waypoint..."
                rows={4}
              />
            </div>

            {/* Phase 6: Enhanced photo display with lightbox */}
            {currentPhotoCount > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Associated Photos</Label>
                  <span className="text-xs text-muted-foreground">
                    {currentPhotoCount} photo{currentPhotoCount !== 1 ? 's' : ''} linked
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(currentPhotoUrls as string[]).map((url, idx) => (
                    <button
                      key={idx}
                      onClick={() => openLightbox(currentPhotoUrls as string[], idx)}
                      className="relative group cursor-zoom-in"
                    >
                      <img 
                        src={url} 
                        alt={`Waypoint photo ${idx + 1}`}
                        className="w-full h-24 object-cover rounded border photo-signature transition-opacity group-hover:opacity-80"
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <ZoomIn className="h-6 w-6 text-white drop-shadow-lg" />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>

        {/* Sticky Footer */}
        <CardFooter className="sticky bottom-0 z-10 bg-card border-t p-4 md:relative md:border-0 md:p-6">
          {/* Mobile: Inline icon buttons */}
          <div className="flex items-center justify-between w-full md:hidden">
            <Button 
              variant="outline" 
              onClick={onBack}
              size="icon"
              className="h-9 w-9"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                onClick={handleDelete}
                size="icon"
                className="h-9 w-9 text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>

              {currentIndex > 0 && (
                <Button 
                  variant="outline" 
                  onClick={handlePrevious}
                  size="icon"
                  className="h-9 w-9"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              )}

              <Button 
                onClick={handleNext}
                size="icon"
                className="h-9 w-9"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Desktop: Full buttons with labels */}
          <div className="hidden md:flex md:flex-row md:justify-between w-full">
            <Button 
              variant="outline" 
              onClick={onBack}
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            
            <div className="flex flex-row gap-2">
              <Button 
                variant="ghost" 
                onClick={handleDelete}
                size="sm"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>

              {currentIndex > 0 && (
                <Button 
                  variant="outline" 
                  onClick={handlePrevious}
                >
                  <ChevronLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
              )}

              <Button onClick={handleNext}>
                {currentIndex === waypoints.length - 1 ? (
                  <>
                    Review All
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardFooter>
      </Card>

      {/* Photo Lightbox */}
      {lightboxOpen && currentPhotoCount > 0 && (
        <PhotoLightbox
          photos={currentPhotoUrls as string[]}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

export default RunWaypointReview;
